import { ComponentInterface } from '@fusioncharts/core/src/component-interface';
import {
  pluckNumber,
  pluck,
  parseUnsafeString,
  parseTooltext,
  getValidValue,
  getDashStyle,
  getDefinedColor,
  BLANK,
  BLANKSTRING,
  setLineHeight,
  preDefStr,
  toRaphaelColor,
  isIE,
  hasTouch,
  getFirstValue,
  TOUCH_THRESHOLD_PIXELS,
  CLICK_THRESHOLD_PIXELS,
  convertColor, getLightColor, getColumnColor, configureDataLabelStyle
} from '@fusioncharts/core/src/lib';
import { addDep } from '@fusioncharts/core/src/dependency-manager';
import heatmapAnimation from './index.animation';
import { priorityList } from '@fusioncharts/core/src/schedular';

let HUNDREDSTRING = '100',
  PLOTGRADIENTCOLOR = 'plotGradientColor',
  PLOTBORDERCOLOR = 'plotBorderColor',
  COLOR_FFFFFF = 'FFFFFF',
  SHOWSHADOW = 'showShadow',
  UNDEF,
  MOUSEOVER = 'mouseOver',
  MOUSEOUT = 'mouseOut',
  NORMAL = 'normal',
  PXSTRING = 'px',
  pStr = preDefStr.pStr,
  sStr = preDefStr.sStr,
  COMMA = ',',
  BOLDSTARTTAG = '<b>',
  BOLDENDTAG = '</b>',
  BREAKSTRING = '<br />',
  visibleStr = 'visible',
  miterStr = preDefStr.miterStr,
  hiddenStr = preDefStr.hiddenStr,
  showHoverEffectStr = preDefStr.showHoverEffectStr,
  POSITION_START = preDefStr.POSITION_START,
  POSITION_TOP = preDefStr.POSITION_TOP,
  POSITION_END = preDefStr.POSITION_END,
  POSITION_BOTTOM = preDefStr.POSITION_BOTTOM,
  SETROLLOVERATTR = 'setRolloverAttr',
  SETROLLOUTATTR = 'setRolloutAttr',
  EVENTARGS = 'eventArgs',
  POINTER = 'pointer',
  tlLabelStr = 'tlLabel',
  blLabelStr = 'blLabel',
  trLabelStr = 'trLabel',
  brLabelStr = 'brLabel',
  ROLLOVER = 'DataPlotRollOver',
  ROLLOUT = 'DataPlotRollOut',
  DEFAULT_CURSOR = preDefStr.DEFAULT,
  MAX_PERCENT_SPACE = 0.9,
  POINT_FIVE = 0.5,
  GUTTER_4 = 4,
  defined = function (obj) {
    return obj !== UNDEF && obj !== null;
  },
  /**
   * Helper function to create a RedRaphael group.
   * @param  {Object} attr  Attributes to be applied to the group
   * @param  {Element} parentContainer         The parent container in which the group will be appended.
   * @param {Object} dataset The concerned dataset
   * @return {Element}                         The group that was created.
   */
  createGroup = function (attr, parentContainer, dataset) {
    return dataset.getFromEnv('animationManager').setAnimation({
      el: 'group',
      attr: attr,
      container: parentContainer,
      component: dataset,
      label: 'group'
    });
  },
  TRACKER_FILL = 'rgba(192,192,192,' + (isIE ? 0.002 : 0.000001) + ')', // invisible but clickable
  HTP = hasTouch ? TOUCH_THRESHOLD_PIXELS
    : CLICK_THRESHOLD_PIXELS,
  pInt = function (s, mag) {
    return parseInt(s, mag || 10);
  },
  NONE = 'none',
  _rolloverResponseSetter = function (chart, data, event, onlyHoverEffect, dataset) {
    var dataGraphics = data.graphics,
      animationManager = chart.getFromEnv('animationManager'),
      elem = dataGraphics && dataGraphics.element,
      elData = elem && elem.getData();
    // Check whether the plot is in dragged state or not if
    // drag then dont fire rolloverevent
    if (elData.draged !== true) {
      animationManager.setAnimationState(MOUSEOVER);
      elem && elData.showHoverEffect !== 0 && animationManager.setAnimation({
        el: elem,
        attr: elem.getData().setRolloverAttr,
        component: dataset
      });
      !onlyHoverEffect && elem && chart.plotEventHandler(elem, event, ROLLOVER);
    }
  },
  _rolloutResponseSetter = function (chart, data, event, onlyHoverEffect, dataset) {
    var dataGraphics = data.graphics,
      animationManager = chart.getFromEnv('animationManager'),
      elem = dataGraphics && dataGraphics.element,
      elData = elem && elem.getData();
    // Check whether the plot is in draggedstate or not if drag then dont fire rolloutevent
    if (elData.draged !== true) {
      animationManager.setAnimationState(MOUSEOUT);
      elem && elData.showHoverEffect !== 0 && animationManager.setAnimation({
        el: elem,
        attr: elem.getData().setRolloutAttr,
        component: dataset
      });
      !onlyHoverEffect && elem && chart.plotEventHandler(elem, event, ROLLOUT);
    }
  },
  mathMax = Math.max,
  mathMin = Math.min,
  mathAbs = Math.abs;
addDep({
  name: 'heatmapAnimation',
  type: 'animationRule',
  extension: heatmapAnimation
});
class HeatMapDataset extends ComponentInterface {
  constructor () {
    super();

    this.components = {};
    this.graphics = {};
    this.dataSetArray = [];
    // this.getDataLimits = null;
  }
  /**
   * Sets the type of the component
   * @return {string} type
   */
  getType () {
    return 'dataset';
  }
  /**
   * Sets the name of the component
   * @return {string} name
   */
  getName () {
    return 'heatMap';
  }
  /**
   * Configures the cosmetic of dataset
   * Creates the empty plotGrid array
   * Calculates the dataset min and max value
   * @param {Object} datasetJSON data array containing data information
   */
  configureAttributes (datasetJSON) {
    if (!datasetJSON) {
      return false;
    }
    this.trimData(datasetJSON);
    this.config.JSONData = datasetJSON;

    var dataSet = this,
      chart = dataSet.getFromEnv('chart'),
      conf = dataSet.config,
      JSONData = conf.JSONData,
      setDataArr = JSONData.data,
      len = setDataArr && setDataArr.length, // eslint-disable-line good-practices/no-single-usage-variable
      chartAttr = dataSet.getFromEnv('chart-attrib'),
      colorM = dataSet.getFromEnv('color-manager'),
      plotBorderDash = pluckNumber(JSONData.dashed, chartAttr.plotborderdashed), // eslint-disable-line good-practices/no-single-usage-variable
      setData,
      setValue,
      dataObj,
      config,
      dataStore = dataSet.components.data,
      plotGrid = dataSet.components.plotGrid = [],
      isBar = chart.isBar,
      is3D = chart.config.is3D,
      enableAnimation,
      i,
      r,
      c,
      maxValue = -Infinity,
      minValue = +Infinity,
      totalRows = dataSet.getFromEnv('totalRows'), // eslint-disable-line good-practices/no-single-usage-variable
      totalColumns = dataSet.getFromEnv('totalColumns'); // eslint-disable-line good-practices/no-single-usage-variable

    for (r = 0; r < totalRows; r++) {
      plotGrid.push([]);
      for (c = 0; c < totalColumns; c++) {
        plotGrid[r].push([]);
      }
    }
    dataSet.setState('visible',
      pluckNumber(dataSet.getState('visible'), dataSet.config.JSONData.visible,
        !Number(dataSet.config.JSONData.initiallyhidden), 1) === 1);
    conf.showplotborder = pluckNumber(chartAttr.showplotborder, is3D ? 0 : 1);
    conf.plotDashLen = pluckNumber(chartAttr.plotborderdashlen, 5);
    conf.plotDashGap = pluckNumber(chartAttr.plotborderdashgap, 4);
    conf.showTextOutLine = pluckNumber(chartAttr.textoutline, 0);
    conf.plotfillAngle = pluckNumber(360 - chartAttr.plotfillangle, (isBar ? 180 : 90));
    conf.plotFillAlpha = pluck(JSONData.alpha, chartAttr.plotfillalpha, HUNDREDSTRING);
    conf.plotColor = pluck(JSONData.color, colorM.getPlotColor(dataSet.index || dataSet.positionIndex));
    conf.isRoundEdges = pluckNumber(chartAttr.useroundedges, 0);
    conf.plotRadius = pluckNumber(chartAttr.useRoundEdges, conf.isRoundEdges ? 1 : 0);
    conf.plotFillRatio = pluck(JSONData.ratio, chartAttr.plotfillratio);
    conf.plotgradientcolor = getDefinedColor(chartAttr.plotgradientcolor,
      colorM.getColor(PLOTGRADIENTCOLOR));
    // !usePlotGradientColor && (plotgradientcolor = BLANKSTRING);
    conf.plotBorderAlpha = conf.showplotborder ? pluck(chartAttr.plotborderalpha,
      conf.plotFillAlpha, HUNDREDSTRING) : 0;
    conf.plotBorderColor = pluck(chartAttr.plotbordercolor,
      is3D ? COLOR_FFFFFF : colorM.getColor(PLOTBORDERCOLOR));
    conf.plotBorderThickness = pluckNumber(chartAttr.plotborderthickness, 1);
    conf.plotBorderDashStyle = plotBorderDash
      ? getDashStyle(conf.plotDashLen, conf.plotDashGap) : NONE;
    conf.showValues = pluckNumber(JSONData.showvalues, chartAttr.showvalues, 1);
    conf.valuePadding = pluckNumber(chartAttr.valuepadding, 2);
    conf.enableAnimation = enableAnimation = pluckNumber(chartAttr.animation,
      chartAttr.defaultanimation, 1);
    conf.animation = !enableAnimation ? false : {
      duration: pluckNumber(chartAttr.animationduration, 1) * 1000
    };
    conf.transposeAnimation =
                pluckNumber(chartAttr.transposeanimation, enableAnimation);
    conf.transposeAnimDuration = pluckNumber(chartAttr.transposeanimduration, 0.2) * 1000;

    conf.showShadow = (conf.isRoundEdges || is3D) ? pluckNumber(chartAttr.showshadow, 1)
      : pluckNumber(chartAttr.showshadow, colorM.getColor(SHOWSHADOW));
    conf.showHoverEffect = pluckNumber(chartAttr.plothovereffect,
      chartAttr.showhovereffect, UNDEF);
    conf.showTooltip = pluckNumber(chartAttr.showtooltip, 1);
    conf.definedGroupPadding = Math.max(pluckNumber(chartAttr.plotspacepercent), 0);
    conf.plotSpacePercent = Math.max(pluckNumber(chartAttr.plotspacepercent, 20) % 100, 0);
    conf.maxColWidth = pluckNumber(isBar ? chartAttr.maxbarheight : chartAttr.maxcolwidth, 50);
    conf.plotPaddingPercent = pluckNumber(chartAttr.plotpaddingpercent);
    conf.rotateValues = pluckNumber(chartAttr.rotatevalues) ? 270 : 0;
    conf.placeValuesInside = pluckNumber(chartAttr.placevaluesinside, 0);
    conf.defaultPadding = {
      left: 0.5,
      right: 0.5,
      top: 0.5,
      bottom: 0.5
    };

    conf.use3DLighting = pluckNumber(chartAttr.use3dlighting, 1);
    conf.parentYAxis = pluck(JSONData.parentyaxis && JSONData.parentyaxis.toLowerCase(),
      pStr) === sStr ? 1 : 0;
    if (!dataStore) {
      dataStore = dataSet.components.data = [];
    }

    // Parsing the attributes and values at set level.
    for (i = 0; i < len; i++) {
      setData = setDataArr && setDataArr[i];
      dataObj = dataStore[i];
      config = dataObj && dataObj.config;

      if (!dataObj) {
        dataObj = dataStore[i] = {
          graphics: {}
        };
      }

      if (!dataObj.config) {
        config = dataStore[i].config = {};
      }

      config.setValue = setValue = dataSet.getFromEnv('number-formatter').getCleanValue(setData.value);
      maxValue = Math.max(maxValue, setValue);
      minValue = Math.min(minValue, setValue);
    }

    conf.maxValue = maxValue;
    conf.minValue = minValue;

    dataSet._setConfigure();
    dataSet.setState('dirty', true);
  }
  /**
   * Attaches legendUpdate event listener to dataset which decides whether to
   * show/hide particular data in dataset.
   * Sets the color configuration for each data objects present in dataset.
   */
  _setConfigure () {
    var dataSet = this,
      chart = dataSet.getFromEnv('chart'),
      conf = dataSet.config,
      JSONData = dataSet.config.JSONData,
      setDataArr = JSONData.data,
      len = setDataArr && setDataArr.length, // eslint-disable-line good-practices/no-single-usage-variable
      chartConfig = dataSet.getFromEnv('chartConfig'),
      chartAttr = dataSet.getFromEnv('chart-attrib'),
      showplotborder = conf.showplotborder,
      plotColor = conf.plotColor = dataSet.getFromEnv('color-manager').getPlotColor(dataSet.index || dataSet.positionIndex),
      showTooltip = pluckNumber(chartAttr.showtooltip, 1), // eslint-disable-line good-practices/no-single-usage-variable
      tooltipSepChar = parseUnsafeString(pluck(chartAttr.tooltipsepchar, ': ')),
      usePlotGradientColor = pluckNumber(chartAttr.useplotgradientcolor, 1), // eslint-disable-line good-practices/no-single-usage-variable
      formatedVal,
      parserConfig,
      setTooltext,
      macroIndices,
      toolText = BLANKSTRING,
      plotDashLen = conf.plotDashLen,
      plotDashGap = conf.plotDashGap,
      plotBorderThickness = conf.plotBorderThickness,
      isRoundEdges = conf.isRoundEdges,
      showHoverEffect = conf.showHoverEffect,
      plotfillAngle = conf.plotfillAngle,
      plotFillAlpha = conf.plotFillAlpha,
      plotFillRatio = conf.plotFillRatio,
      plotBorderAlpha = conf.plotBorderAlpha,
      plotBorderColor = conf.plotBorderColor,
      plotBorderDashStyle,
      setData,
      setValue,
      dataObj,
      config,
      colorRange = dataSet.getFromEnv('chartColorRange'),
      column,
      row,
      rowid,
      columnid,
      mapByPercent = conf.mapByPercent = pluckNumber(colorRange.mapbypercent, 0),
      mapByCategory = conf.mapByCategory =
                    pluckNumber(chartAttr.mapbycategory, 0),
      useColorGradient = colorRange && pluckNumber(colorRange.gradient), // eslint-disable-line good-practices/no-single-usage-variable
      hoverColor,
      hoverAlpha,
      hoverGradientColor,
      hoverRatio,
      hoverAngle,
      hoverBorderColor,
      hoverBorderAlpha,
      hoverBorderThickness,
      hoverBorderDashed,
      hoverBorderDashGap,
      hoverBorderDashLen,
      hoverDashStyle,
      hoverColorArr,
      numberFormatter = dataSet.getFromEnv('number-formatter'),
      setDisplayValue,
      isBar = chart.isBar,
      is3D = chart.config.is3D,
      parentYAxis = conf.parentYAxis,
      i,
      maxValue = conf.maxValue, // eslint-disable-line good-practices/no-single-usage-variable
      minValue = conf.minValue,
      heatRange,
      colorObj,
      tlType = getValidValue(chartAttr.tltype, BLANK),
      trType = getValidValue(chartAttr.trtype, BLANK),
      blType = getValidValue(chartAttr.bltype, BLANK),
      brType = getValidValue(chartAttr.brtype, BLANK),
      tlLabel,
      trLabel,
      blLabel,
      brLabel,
      tlTypeToolText = BLANK, // eslint-disable-line good-practices/no-single-usage-variable
      trTypeToolText = BLANK, // eslint-disable-line good-practices/no-single-usage-variable
      blTypeToolText = BLANK, // eslint-disable-line good-practices/no-single-usage-variable
      brTypeToolText = BLANK, // eslint-disable-line good-practices/no-single-usage-variable
      percentValue,
      toolTextValue,
      value,
      minMaxArray,
      minMaxArrayLen,
      toolTipValue,
      setDataDashed,
      setDataPlotDashLen,
      setDataPlotDashGap,
      colorManager = chart.getFromEnv('colorManager'),
      font,
      fontColor,
      fontSize,
      fontWeight,
      fontStyle;

    font = chartConfig.style.inCanfontFamily;
    fontSize = pInt(chartConfig.style.inCanfontSize, 10);
    fontColor = chartConfig.style.inCancolor;
    fontWeight = NORMAL;
    fontStyle = NORMAL;

    heatRange = maxValue - minValue; // eslint-disable-line good-practices/no-single-usage-variable
    if (!conf.eventAttached) {
      // listen to event fired by legends and decide what to do
      dataSet.addExtEventListener('legendUpdate', function (e, d) {
        if (d.component === 'legend') {
          if (d.legendItem.hasState('hidden')) {
            dataSet.show(d.legendItem, e);
          } else {
            dataSet.hide(d.legendItem, e);
          }
        } else {
          minMaxArray = d.maxMinArray;
          minMaxArrayLen = minMaxArray.length;
          for (i = 0; i < minMaxArrayLen; i++) {
            dataSet.updatePlot(minMaxArray[i].min, minMaxArray[i].max);
          }
        }
      }, colorManager);
      conf.eventAttached = true;
    }

    if (!useColorGradient || mapByCategory) {
      conf.colorMap = [];
      if (colorRange.color) {
        for (i = 0; i < colorRange.color.length; i++) {
          conf.colorMap[i] = {
            config: colorRange.color[i],
            dataSet: dataSet
          };
          conf.colorMap[i].config.visible = true;
        }
      }
    }

    for (i = 0; i < len; i++) {
      setData = setDataArr && setDataArr[i];
      dataObj = dataSet.components.data[i];
      config = dataObj && dataObj.config;
      setValue = config.setValue;
      config.showValue = pluckNumber(setData.showvalue, conf.showValues);
      config.setLink = pluck(setData.link);
      config.toolTipValue = toolTipValue = numberFormatter.dataLabels(setValue, parentYAxis);
      config.setDisplayValue = setDisplayValue = parseUnsafeString(setData.displayvalue);
      config.displayValue = pluck(setDisplayValue, toolTipValue);
      setDataDashed = pluckNumber(setData.dashed);
      setDataPlotDashLen = pluckNumber(setData.dashlen, plotDashLen);
      setDataPlotDashGap = plotDashGap = pluckNumber(setData.dashgap, plotDashGap);
      config.plotBorderDashStyle = setDataDashed === 1
        ? getDashStyle(setDataPlotDashLen, setDataPlotDashGap)
        : (setDataDashed === 0 ? NONE : conf.plotBorderDashStyle);
      /* @TODO-
      * Heatmap percentage calculation is done for each dataset individually and shown for ease.
      * But ideally percentage calculation must be done considering all the datasets.
      * A heatmap group manager should query each dataset for their local min and max values and
      * calculate overall range. Then, the group must inform all the dataset the range and the dataset must
      * calculate the percentage values for each data and then draw itself.
      */
      if (mapByPercent) {
        config.percentValue = percentValue = setData.value && (Math.round((setData.value -
                                                minValue) / heatRange * 10000) / 100);
      } else {
        config.percentValue = UNDEF;
      }

      config.dataLabelStyle = dataSet._configureDataLabelStyle(setData);

      config.value = value = (mapByCategory /* || !useColorGradient */)
        ? (setData.colorrangelabel || setData.categoryid) : mapByPercent ? percentValue : config.setValue;
      colorObj = colorManager.getColor(value);
      config.colorRangeLabel = setData.colorrangelabel || colorObj.label;
      dataObj.legendItemIndex = colorObj.seriesIndex;
      config.legendItemId = colorObj.legendItemId;
      colorObj && !colorObj.code && (colorObj.code = 'ffffff');
      colorObj && colorObj.outOfRange && (colorObj.code = 'ffffff');

      // alpha at data label > alpha at color object > plotfillalpha
      config.plotFillAlpha = plotFillAlpha = pluckNumber(setData.alpha, colorObj && colorObj.oriAlpha, conf.plotFillAlpha);
      plotColor = pluck(setData.color, (colorObj && colorObj.code));
      if (setValue < 0 && !isRoundEdges) {
        plotfillAngle = isBar ? 180 - plotfillAngle : 360 - plotfillAngle;
      }
      config.colorArr = getColumnColor(
        plotColor + COMMA + conf.plotgradientcolor,
        plotFillAlpha.toString(),
        plotFillRatio,
        plotfillAngle,
        isRoundEdges,
        plotBorderColor,
        plotBorderAlpha.toString(),
        (isBar ? 1 : 0),
        (!!is3D)
      );

      config.visible = true;

      config.color = convertColor(plotColor, config.plotFillAlpha);
      config.rawPlotColor = plotColor;

      // Parsing the hover effects only if showhovereffect is not 0.
      if (showHoverEffect !== 0) {
        hoverColor = pluck(setData.hovercolor, JSONData.hovercolor, chartAttr.plotfillhovercolor,
          chartAttr.columnhovercolor, plotColor);
        hoverAlpha = pluck(setData.hoveralpha, JSONData.hoveralpha,
          chartAttr.plotfillhoveralpha, chartAttr.columnhoveralpha, '25');
        hoverGradientColor = pluck(setData.hovergradientcolor,
          JSONData.hovergradientcolor, chartAttr.plothovergradientcolor, !usePlotGradientColor && BLANKSTRING);
        !hoverGradientColor && (hoverGradientColor = BLANKSTRING);
        hoverRatio = pluck(setData.hoverratio,
          JSONData.hoverratio, chartAttr.plothoverratio, plotFillRatio);
        hoverAngle = pluckNumber(360 - setData.hoverangle,
          360 - JSONData.hoverangle, 360 - chartAttr.plothoverangle, plotfillAngle);
        hoverBorderColor = pluck(setData.borderhovercolor,
          JSONData.borderhovercolor, chartAttr.plotborderhovercolor, plotBorderColor);
        hoverBorderAlpha = pluck(setData.borderhoveralpha,
          JSONData.borderhoveralpha, chartAttr.plotborderhoveralpha, plotBorderAlpha, plotFillAlpha);
        hoverBorderThickness = pluckNumber(setData.borderhoverthickness,
          JSONData.borderhoverthickness, chartAttr.plotborderhoverthickness, plotBorderThickness);
        hoverBorderDashed = pluckNumber(setData.borderhoverdashed,
          JSONData.borderhoverdashed, chartAttr.plotborderhoverdashed);
        hoverBorderDashGap = pluckNumber(setData.borderhoverdashgap,
          JSONData.borderhoverdashgap, chartAttr.plotborderhoverdashgap, plotDashLen);
        hoverBorderDashLen = pluckNumber(setData.borderhoverdashlen,
          JSONData.borderhoverdashlen, chartAttr.plotborderhoverdashlen, plotDashGap);
        hoverDashStyle = hoverBorderDashed
          ? getDashStyle(hoverBorderDashLen, hoverBorderDashGap)
          : conf.plotBorderDashStyle;

        /* If no hover effects are explicitly defined and
                     * showHoverEffect is not 0 then hoverColor is set.
                     */
        if (showHoverEffect === 1 && hoverColor === plotColor) {
          hoverColor = getLightColor(hoverColor, 70);
        }

        // setting the hover color array which is always applied except when showHoverEffect is not 0.
        hoverColorArr = getColumnColor(
          hoverColor,
          hoverAlpha,
          hoverRatio,
          hoverAngle,
          isRoundEdges,
          hoverBorderColor,
          hoverBorderAlpha.toString(),
          (isBar ? 1 : 0),
          (!!is3D)
        );

        config.setRolloutAttr = {
          fill: toRaphaelColor(config.color),
          stroke: (showplotborder && toRaphaelColor(config.colorArr[1])) || TRACKER_FILL,
          'stroke-width': plotBorderThickness,
          'stroke-dasharray': plotBorderDashStyle || []
        };
        config.setRolloverAttr = {
          fill: toRaphaelColor(hoverColorArr[0]),
          stroke: (showplotborder && toRaphaelColor(hoverColorArr[1])) || TRACKER_FILL,
          'stroke-width': hoverBorderThickness,
          'stroke-dasharray': hoverDashStyle
        };
      }

      mapByPercent && (percentValue = numberFormatter.percentValue(percentValue));

      config.setValue = setValue = numberFormatter.getCleanValue(setData.value);
      config.toolTipValue = numberFormatter.dataLabels(setValue, parentYAxis);
      formatedVal = config.toolTipValue;
      setTooltext = getValidValue(parseUnsafeString(pluck(setData.tooltext,
        JSONData.plottooltext, chartAttr.plottooltext), false));

      config.tlLabel = tlLabel = parseUnsafeString(pluck(setData.tllabel, setData.ltlabel));
      config.trLabel = trLabel = parseUnsafeString(pluck(setData.trlabel, setData.rtlabel));
      config.blLabel = blLabel = parseUnsafeString(pluck(setData.bllabel, setData.lblabel));
      config.brLabel = brLabel = parseUnsafeString(pluck(setData.brlabel, setData.rblabel));

      config.tlLabelStyle = {
        fontFamily: pluck(colorObj.tlLabelFont, chartAttr.tlfont, font),
        fontSize: pluckNumber(colorObj.tlLabelFontSize, chartAttr.tlfontsize, fontSize) + PXSTRING,
        color: convertColor(pluck(colorObj.tlLabelFontColor, chartAttr.tlfontcolor, fontColor), 100),
        fontWeight: fontWeight,
        fontStyle: fontStyle
      };
      setLineHeight(config.tlLabelStyle);
      config.trLabelStyle = {
        fontFamily: pluck(colorObj.trLabelFont, chartAttr.trfont, font),
        fontSize: pluckNumber(colorObj.trLabelFontSize, chartAttr.trfontsize, fontSize) + PXSTRING,
        color: convertColor(pluck(colorObj.trLabelFontColor, chartAttr.trfontcolor, fontColor), 100),
        fontWeight: fontWeight,
        fontStyle: fontStyle
      };

      config.brLabelStyle = {
        fontFamily: pluck(colorObj.brLabelFont, chartAttr.brfont, font),
        fontSize: pluckNumber(colorObj.brLabelFontSize, chartAttr.brfontsize, fontSize) + PXSTRING,
        color: convertColor(pluck(colorObj.brLabelFontColor, chartAttr.brfontcolor, fontColor), 100),
        fontWeight: fontWeight,
        fontStyle: fontStyle
      };

      config.blLabelStyle = {
        fontFamily: pluck(colorObj.blLabelFont, chartAttr.blfont, font),
        fontSize: pluckNumber(colorObj.blLabelFontSize, chartAttr.blfontsize, fontSize) + PXSTRING,
        color: convertColor(pluck(colorObj.blLabelFontColor, chartAttr.blfontcolor, fontColor), 100),
        fontWeight: fontWeight,
        fontStyle: fontStyle
      };

      setDisplayValue = getValidValue(parseUnsafeString(
        setData.displayvalue));
      toolTextValue = mapByCategory
        ? setDisplayValue : pluck(setData.displayvalue, formatedVal);

      config.displayValue = pluck(setDisplayValue, percentValue, config.toolTipValue);

      if (tlType !== BLANK) {
        tlTypeToolText = BOLDSTARTTAG + tlType + tooltipSepChar + BOLDENDTAG;
      }
      if (trType !== BLANK) {
        trTypeToolText = BOLDSTARTTAG + trType + tooltipSepChar + BOLDENDTAG;
      }
      if (blType !== BLANK) {
        blTypeToolText = BOLDSTARTTAG + blType + tooltipSepChar + BOLDENDTAG;
      }
      if (brType !== BLANK) {
        brTypeToolText = BOLDSTARTTAG + brType + tooltipSepChar + BOLDENDTAG;
      }
      columnid = getFirstValue(setDataArr[i].columnid);
      rowid = getFirstValue(setDataArr[i].rowid);
      column = dataSet.getFromEnv('xAxis').getCategoryFromId(columnid.toLowerCase());
      row = dataSet.getFromEnv('yAxis').getCategoryFromId(rowid.toLowerCase());

      // create the tooltext
      if (showTooltip) {
        if (formatedVal === null) {
          toolText = false;
        } else if (setTooltext !== UNDEF) {
          macroIndices = [1, 2, 5, 6, 7, 14, 93, 94, 95, 96, 97, 98, 112, 113, 114, 115, 116,
            117];
          parserConfig = {
            formattedValue: formatedVal,
            value: setData.value,
            yaxisName: parseUnsafeString(chartAttr.yaxisname),
            xaxisName: parseUnsafeString(chartAttr.xaxisname),
            displayValue: setDisplayValue,
            percentValue: mapByPercent ? percentValue : BLANK,
            tlLabel: tlLabel,
            trLabel: trLabel,
            blLabel: blLabel,
            brLabel: brLabel,
            rowLabel: row.tickObj && row.tickObj.label,
            columnLabel: column.tickObj && column.tickObj.label,
            percentDataValue: mapByPercent ? percentValue : BLANK,
            trtype: trType,
            tltype: tlType,
            brType: brType,
            blType: blType,
            colorRangeLabel: config.colorRangeLabel
          };
          toolText = parseTooltext(setTooltext, macroIndices,
            parserConfig, setData, chartAttr, parserConfig);
        } else {
          toolText = ((mapByPercent ? BOLDSTARTTAG + 'Value' + tooltipSepChar + BOLDENDTAG +
                            formatedVal +
                            BREAKSTRING + BOLDSTARTTAG + 'Percentage' + tooltipSepChar + BOLDENDTAG +
                            percentValue
            : toolTextValue) +
                        // Now we add special labels in toolTip
                        (tlLabel !== BLANK ? BREAKSTRING +
                            (tlTypeToolText + tlLabel) : BLANK) + (trLabel !== BLANK ? BREAKSTRING +
                            trTypeToolText + trLabel : BLANK) + (blLabel !== BLANK ? BREAKSTRING +
                            blTypeToolText + blLabel : BLANK) + (brLabel !== BLANK ? BREAKSTRING +
                            brTypeToolText + brLabel : BLANK));
        }
      }
      config.toolText = toolText;
      config.setTooltext = toolText;
    }
  }
  /**
   * Hides the data objects according to the legend item
   * @param {Object} item - legend item
   */
  hide (item) {
    var dataset = this,
      entityComponents = dataset.components.data,
      colorRange = dataset.getFromEnv('chart').getChildren('colorRange')[0], // eslint-disable-line good-practices/no-single-usage-variable
      i,
      len,
      config,
      legendItemColor,
      colorCode;

    legendItemColor = item.config.datasetObj.code; // eslint-disable-line good-practices/no-single-usage-variable

    for (i = 0, len = entityComponents.length; i < len; i++) { // eslint-disable-line good-practices/no-single-usage-variable
      if (!Object.keys(entityComponents[i]).length) {
        continue;
      }
      colorCode = colorRange.getColorObj(entityComponents[i].config.value).code;
      config = entityComponents[i].config;

      if (legendItemColor === colorCode) {
        entityComponents[i].graphics.element && dataset.getFromEnv('animationManager').setAnimation({
          el: entityComponents[i].graphics.element,
          attr: {
            'fill-opacity': 0,
            'stroke-width': 0
          },
          component: dataset
        });

        // animFlagHide = false;

        entityComponents[i].graphics.hotElement && entityComponents[i].graphics.hotElement.hide();
        config.visible = false;
        entityComponents[i].visible = false;
      }
    }
    item.setLegendState('hidden');
  }
  /**
   * Shows the data objects according to the legend item
   * @param {Object} item - legend item
   */
  show (item) {
    var dataset = this,
      entityComponents = dataset.components.data,
      animationManager = dataset.getFromEnv('animationManager'),
      config,
      plotFillAlpha,
      colorObj,
      colorRange = dataset.getFromEnv('chart').getChildren('colorRange')[0], // eslint-disable-line good-practices/no-single-usage-variable
      i,
      len,
      legendItemColor,
      colorCode;

    legendItemColor = item.config.datasetObj.code; // eslint-disable-line good-practices/no-single-usage-variable

    for (i = 0, len = entityComponents.length; i < len; i++) { // eslint-disable-line good-practices/no-single-usage-variable
      if (!Object.keys(entityComponents[i]).length) {
        continue;
      }
      config = entityComponents[i].config;
      plotFillAlpha = config.plotFillAlpha / 100;
      colorObj = colorRange.getColorObj(entityComponents[i].config.value);
      //  do not set color for out of range values
      colorCode = !colorObj.outOfRange && colorObj.code;

      if (legendItemColor === colorCode) {
        entityComponents[i].graphics.element && animationManager.setAnimation({
          el: entityComponents[i].graphics.element,
          attr: {
            'visibility': visibleStr
          },
          component: dataset
        });
        entityComponents[i].graphics.element && animationManager.setAnimation({
          el: entityComponents[i].graphics.element,
          component: dataset,
          attr: {
            'fill-opacity': plotFillAlpha,
            'stroke-width': dataset.config.plotBorderThickness
          }
        });

        // animFlagShow = false;

        entityComponents[i].graphics.hotElement &&
                        entityComponents[i].graphics.hotElement.show();
        entityComponents[i].graphics.valEle && entityComponents[i].graphics.valEle.show();

        entityComponents[i].graphics.tlLabel && entityComponents[i].graphics.tlLabel.show();
        entityComponents[i].graphics.trLabel && entityComponents[i].graphics.trLabel.show();
        entityComponents[i].graphics.blLabel && entityComponents[i].graphics.blLabel.show();
        entityComponents[i].graphics.brLabel && entityComponents[i].graphics.brLabel.show();
        config.visible = true;
        entityComponents[i].visible = true;
      }
    }
    item && item.removeLegendState('hidden');
  }
  /**
   * updates the plot range when gradient legend is moved
   */
  updatePlot (minValue, maxValue) {
    var dataset = this,
      animationManager = dataset.getFromEnv('animationManager'),
      config,
      plotFillAlpha,
      entityComponents = dataset.components.data,
      i,
      len,
      value;

    for (i = 0, len = entityComponents.length; i < len; i++) { // eslint-disable-line good-practices/no-single-usage-variable
      if (!Object.keys(entityComponents[i]).length) {
        continue;
      }
      config = entityComponents[i].config;
      plotFillAlpha = config.plotFillAlpha / 100;

      value = entityComponents[i].config.value;

      if (value < minValue || value > maxValue) {
        if (config.visible) {
          entityComponents[i].graphics.element && animationManager.setAnimation({
            el: entityComponents[i].graphics.element,
            attr: {
              'fill-opacity': 0,
              'stroke-width': 0
            },
            component: dataset
          });
          entityComponents[i].graphics.hotElement && entityComponents[i].graphics.hotElement.hide();
          config.visible = false;
          entityComponents[i].visible = false;
        }
      } else {
        if (!config.visible) {
          // entityComponents[i].graphics.element && animationManager.registerAnimation([{
          //   animType: animType,
          //   data: [{
          //     el: entityComponents[i].graphics.element,
          //     attrs: {
          //       'fill-opacity': plotFillAlpha,
          //       'stroke-width': conf.plotBorderThickness
          //     }
          //   }]
          // }], 'plot');
          entityComponents[i].graphics.element && animationManager.setAnimation({
            el: entityComponents[i].graphics.element,
            attr: {
              'fill-opacity': plotFillAlpha,
              'stroke-width': dataset.config.plotBorderThickness
            },
            callback: function () {
              this.show();
            },
            component: dataset
          });

          entityComponents[i].graphics.hotElement &&
                            entityComponents[i].graphics.hotElement.show();
          entityComponents[i].graphics.valEle && entityComponents[i].graphics.valEle.show();

          entityComponents[i].graphics.tlLabel && entityComponents[i].graphics.tlLabel.show();
          entityComponents[i].graphics.trLabel && entityComponents[i].graphics.trLabel.show();
          entityComponents[i].graphics.blLabel && entityComponents[i].graphics.blLabel.show();
          entityComponents[i].graphics.brLabel && entityComponents[i].graphics.brLabel.show();
          config.visible = true;
          entityComponents[i].visible = true;
        }
      }
    }
  }

  _checkPointObj (pX, pY, chartX, chartY) {
    var dataset = this,
      plotGrid = dataset.components.plotGrid,
      pointObj,
      chartConfig = dataset.getFromEnv('chartConfig'),
      viewPortConfig = chartConfig.viewPortConfig,
      plotBorderThickness = chartConfig.plotborderthickness,
      halfPlotBorderThickness,
      dx,
      dy,
      hovered;

    pointObj = plotGrid[pY] && plotGrid[pY][pX];
    plotBorderThickness = chartConfig.showplotborder ? plotBorderThickness : 0;
    halfPlotBorderThickness = plotBorderThickness / 2;

    halfPlotBorderThickness = halfPlotBorderThickness % 2 === 0 ? halfPlotBorderThickness + 1
      : Math.round(halfPlotBorderThickness);

    if (pointObj && pointObj.config && pointObj.config.visible) {
      dx = chartX - (pointObj._xPos - viewPortConfig.x * viewPortConfig.scaleX) + halfPlotBorderThickness;
      dy = chartY - pointObj._yPos + halfPlotBorderThickness;

      hovered = dx >= 0 && dx <= pointObj._width + plotBorderThickness && dy >= 0 &&
                    dy <= pointObj._height + plotBorderThickness;

      if (hovered) {
        return {
          pointIndex: pointObj._index,
          hovered: hovered,
          pointObj: pointObj
        };
      }
    }
  }
  /**
   *  function to return the hovered data object
   */
  _getHoveredPlot (chartXVal, chartYVal) {
    var dataset = this,
      chartX = chartXVal,
      chartY = chartYVal,
      xAxis = dataset.getFromEnv('xAxis'),
      yAxis = dataset.getFromEnv('yAxis'),
      height = dataset.getFromEnv('chartConfig').canvasHeight / dataset.getFromEnv('totalRows'), // eslint-disable-line good-practices/no-single-usage-variable
      x,
      y,
      pX,
      pY;
    chartX += xAxis.getTranslation();
    chartY += yAxis.getTranslation();
    y = yAxis.getValue(chartY + height / 2);
    pY = Math.floor(y);

    x = xAxis.getValue(chartX);
    pX = Math.round(x);

    if (pX - x > 0) {
      return (y - pY > 0.5) ? (dataset._checkPointObj(pX, pY, chartX, chartY) ||
                 dataset._checkPointObj(pX - 1, pY, chartX, chartY))
        : (dataset._checkPointObj(pX, pY - 1, chartX, chartY) ||
                    dataset._checkPointObj(pX, pY, chartX, chartY));
    }
    return (y - pY > 0.5) ? (dataset._checkPointObj(pX + 1, pY, chartX, chartY) ||
                    dataset._checkPointObj(pX, pY, chartX, chartY))
      : (dataset._checkPointObj(pX, pY - 1, chartX, chartY) ||
                    dataset._checkPointObj(pX + 1, pY, chartX, chartY) ||
                    dataset._checkPointObj(pX, pY, chartX, chartY));
  }

  /**
   * Parses the plot attributes
   *
   * @param {any} dataObj
   * @param {any} index
   * @returns {any}
   * @memberof HeatMapDataset
   */
  parsePlotAttributes (dataObj, index) {
    var dataSet = this,
      conf = dataSet.config,
      setDataArr = dataSet.config.JSONData.data,
      attr,
      i = index,
      chart = dataSet.getFromEnv('chart'),
      xAxis = dataSet.getFromEnv('xAxis'),
      yAxis = dataSet.getFromEnv('yAxis'),
      xPos,
      yPos,
      chartConfig = dataSet.getFromEnv('chartConfig'),
      columnWidth,
      height,
      toolText,
      setLink,
      setValue,
      config,
      plotBorderThickness = conf.plotBorderThickness,
      plotRadius = conf.plotRadius,
      colorArr,
      plotBorderDashStyle,
      legendActive = chart.getFromEnv('legend') && chart.getFromEnv('legend').config.isActive,
      plotGrid = dataSet.components.plotGrid, // eslint-disable-line good-practices/no-single-usage-variable
      width,
      column,
      row,
      columnid,
      rowid,
      trackerConfig;

    yAxis.yBasePos = yAxis.getPixel(yAxis.getAxisBase());
    if (!dataObj || !Object.keys(dataObj).length) {
      return;
    }
    width = chartConfig.canvasWidth / dataSet.getFromEnv('totalColumns');
    height = chartConfig.canvasHeight / dataSet.getFromEnv('totalRows');

    trackerConfig = dataObj.trackerConfig = {};
    config = dataObj && dataObj.config;
    setValue = config.setValue;
    columnid = getFirstValue(setDataArr[i].columnid);
    rowid = getFirstValue(setDataArr[i].rowid);
    column = xAxis.getCategoryFromId(columnid.toLowerCase());
    row = yAxis.getCategoryFromId(rowid.toLowerCase());
    if ((column.index !== 0 && !column.index) || (row.index !== 0 && !row.index)) {
      return;
    }
    if (!conf.mapByCategory && setValue === null) {
      return;
    }

    setLink = config.setLink;
    colorArr = config.colorArr;

    // Creating the data structure if not present for storing the graphics elements.
    if (!dataObj.graphics) {
      dataSet.components.data[i].graphics = {};
    }

    xPos = xAxis.getPixel(column.index) - width / 2;

    yPos = yAxis.getPixel(row.index) - height / 2;

    columnWidth = width;

    // Setting the final tooltext.
    toolText = config.toolText;

    // Setting the event arguments.
    trackerConfig.eventArgs = {
      index: i,
      link: setLink,
      value: config.percentValue || setValue,
      displayValue: config.displayValue,
      columnId: column.tickObj.id,
      rowId: row.tickObj.id,
      tlLabel: config.tlLabel,
      trLabel: config.trLabel,
      blLabel: config.blLabel,
      brLabel: config.brLabel,
      colorRangeLabel: config.colorRangeLabel,
      color: config.rawPlotColor,
      alpha: config.plotFillAlpha,
      showValue: config.showValue,
      // categoryLabel: categories[i].label,
      toolText: !toolText ? '' : toolText,
      id: BLANKSTRING,
      datasetIndex: legendActive ? dataObj.datasetIndex : UNDEF,
      datasetName: legendActive ? dataObj.datasetName : UNDEF,
      visible: dataSet.getState('visible')
    };

    // Setting the attributes for plot drawing.
    attr = {
      x: xPos,
      y: yPos,
      width: columnWidth,
      height: height || 1,
      r: plotRadius,
      fill: config.color,
      stroke: toRaphaelColor(colorArr[1]),
      'stroke-width': plotBorderThickness,
      'stroke-dasharray': plotBorderDashStyle,
      'fill-opacity': config.plotFillAlpha / 100,
      'stroke-linejoin': miterStr,
      'visibility': config.visible ? visibleStr : hiddenStr,
      cursor: setLink ? POINTER : BLANKSTRING
    };

    // todo- remove _ to make it public
    dataObj._xPos = xPos;
    dataObj._yPos = yPos;
    dataObj._height = height;
    dataObj._width = columnWidth;
    dataObj._index = i;
    plotGrid[row.index][column.index] = dataObj;
    /*
    * If the data plots are not present then they are created, else only attributes are set for the
    * existing plots.
    */
    if (!dataObj.graphics.element) {
      attr['fill-opacity'] = config.plotFillAlpha / 100;
      attr['stroke-width'] = plotBorderThickness;
    } else {
      attr = {
        x: xPos,
        y: yPos,
        width: columnWidth,
        height: height || 1
      };
      attr.fill = config.color;
      attr.stroke = toRaphaelColor(colorArr[1]);
      attr['fill-opacity'] = config.visible ? config.plotFillAlpha / 100 : 0;
      attr['stroke-width'] = config.visible ? plotBorderThickness : 0;
      attr['stroke-dasharray'] = plotBorderDashStyle;
      attr['stroke-linejoin'] = miterStr;
      attr.visibility = config.visible ? visibleStr : hiddenStr;
      attr.cursor = setLink ? POINTER : BLANKSTRING;
    }

    if (setLink || chartConfig.showtooltip) {
      // Fix for touch devices.
      if (height < HTP) {
        yPos -= (HTP - height) / 2;
        height = HTP;
      }

      // Setting attributes for the tooltip.
      trackerConfig.attr = {
        x: xPos,
        y: yPos,
        width: columnWidth,
        height: height,
        r: plotRadius,
        cursor: setLink ? POINTER : BLANKSTRING,
        stroke: TRACKER_FILL,
        'stroke-width': plotBorderThickness,
        fill: TRACKER_FILL,
        'visibility': config.visible ? visibleStr : hiddenStr
      };
    }

    config.props = {
      element: {
        attr: attr
      }
    };
  }

  /**
   * Parses and allocate plot cosmetics and position
   *
   * @memberof HeatMapDataset
   */
  allocatePosition () {
    var dataSet = this,
      setDataArr = dataSet.config.JSONData.data,
      len,
      i,
      dataStore = dataSet.components.data, // eslint-disable-line good-practices/no-single-usage-variable
      dataObj;

    len = setDataArr && setDataArr.length; // eslint-disable-line good-practices/no-single-usage-variable

    for (i = 0; i < len; i++) {
      dataObj = dataStore[i];
      dataSet.parsePlotAttributes(dataObj, i);
      // dataSet.parseLabelAttributes(dataObj, i);
    }
  }
  /**
   * draws the plot according to the configuration
   */
  drawPlots () {
    var dataSet = this,
      conf = dataSet.config,
      setDataArr = dataSet.config.JSONData.data,
      len,
      i,
      animationManager = dataSet.getFromEnv('animationManager'),
      crispBox,
      dataStore = dataSet.components.data, // eslint-disable-line good-practices/no-single-usage-variable
      dataObj,
      setElement,
      setValue,
      config,
      state,
      column,
      row,
      columnid,
      rowid,
      elemIdStore = [], // eslint-disable-line good-practices/no-single-usage-variable
      elemId,
      removeDataArrLen = (dataSet.components.removeDataArr || []).length, // eslint-disable-line good-practices/no-single-usage-variable
      trackerConfig;

    len = setDataArr && setDataArr.length; // eslint-disable-line good-practices/no-single-usage-variable

    // Create plot elements.
    for (i = 0; i < len; i++) {
      dataObj = dataStore[i];
      trackerConfig = dataObj.trackerConfig;
      config = dataObj && dataObj.config;
      if (!dataObj.graphics) {
        continue;
      }
      state = !dataObj.graphics.element ? 'appearing' : dataSet.getState('visible') ? 'updating' : 'hiding';
      setValue = config.setValue;
      columnid = getFirstValue(setDataArr[i].columnid);
      rowid = getFirstValue(setDataArr[i].rowid);
      column = dataSet.getFromEnv('xAxis').getCategoryFromId(columnid.toLowerCase());
      row = dataSet.getFromEnv('yAxis').getCategoryFromId(rowid.toLowerCase());
      if ((column.index !== 0 && !column.index) || (row.index !== 0 && !row.index)) {
        dataObj.graphics.element = dataObj.graphics.element && animationManager.setAnimation({
          el: dataObj.graphics.element,
          component: dataSet
        });
        continue;
      }

      if ((!column.tickObj || !row.tickObj || (config.value === BLANKSTRING)) && dataObj.graphics) {
        dataObj.graphics.element = dataObj.graphics.element && animationManager.setAnimation({
          el: dataObj.graphics.element,
          component: dataSet
        });
      }

      if (!conf.mapByCategory && setValue === null && dataObj.graphics) {
        dataObj.graphics.element && dataObj.graphics.element.hide();
        dataObj.graphics.hotElement && dataObj.graphics.hotElement.hide();
        continue;
      }

      elemId = column.index.toString() + row.index.toString();

      elemIdStore.push(elemId);

      /*
      * If the data plots are not present then they are created, else only attributes are set for the
      * existing plots.
      */
      if (!dataObj.graphics.element) {
        setElement = dataObj.graphics.element = animationManager.setAnimation({
          el: 'rect',
          component: dataSet,
          attr: config.props.element.attr,
          label: 'rect',
          container: dataSet.getContainer('plotGroup')
        });

        config.elemCreated = true;
      } else {
        setElement = dataObj.graphics.element;
        config.elemCreated = false;

        animationManager.setAnimation({
          el: setElement,
          state: state,
          attr: config.props.element.attr,
          component: dataSet
        });
      }

      // The shadow element is set for the dataplots.
      setElement
        .shadow({ opacity: conf.showShadow }, dataSet.getContainer('shadowGroup'))
        .data('BBox', crispBox);

      dataSet.getFromEnv('chartConfig').enablemousetracking && setElement
        .data(EVENTARGS, trackerConfig.eventArgs)
        .data(showHoverEffectStr, conf.showHoverEffect)
        .data(SETROLLOVERATTR, config.setRolloverAttr || {})
        .data(SETROLLOUTATTR, config.setRolloutAttr || {});
    }

    dataSet.drawn ? dataSet.drawLabel() : (dataSet.addJob('labelJob', dataSet.drawLabel.bind(dataSet),
      priorityList.label));
    dataSet.drawn = true;

    removeDataArrLen && dataSet.removeDataElems();
  }
  /**
   * Draws the data labels if any
   */
  drawLabel () {
    var dataSet = this,
      chartConf = dataSet.getFromEnv('chartConfig'),
      smartLabel = dataSet.getFromEnv('smartLabel'),
      animationManager = dataSet.getFromEnv('animationManager'),
      style = chartConf.dataLabelStyle,
      conf = dataSet.config,
      len = (dataSet.config.JSONData.data || []).length, // eslint-disable-line good-practices/no-single-usage-variable
      dataStore = dataSet.components.data, // eslint-disable-line good-practices/no-single-usage-variable
      dataObj,
      i,
      displayValue,
      graphic,
      textY,
      textX,
      yPos,
      xPos,
      attr,
      dataLabelContainer = dataSet.getContainer('labelGroup'),

      tlLabelContainer = dataSet.graphics.tlLabelContainer,
      blLabelContainer = dataSet.graphics.blLabelContainer,
      trLabelContainer = dataSet.graphics.trLabelContainer,
      brLabelContainer = dataSet.graphics.brLabelContainer,
      plotLabelStyle,
      tlStyle,
      trStyle,
      blStyle,
      brStyle,
      graphicEle,
      pointW,
      pointH,
      pointX,
      pointY,
      smartTextObj,
      tlLabel,
      trLabel,
      blLabel,
      brLabel,
      isTLLabel,
      isTRLabel,
      isBLLabel,
      isBRLabel,
      maxWidth,
      maxHeight,
      config,
      setValue,
      tlLabelCss,
      trLabelCss,
      blLabelCss,
      brLabelCss;

    animationManager.setAnimation({
      el: dataLabelContainer,
      attr: {
        opacity: 1
      },
      component: dataSet,
      label: 'text'
    });

    if (!tlLabelContainer) {
      tlLabelContainer = dataSet.graphics.tlLabelContainer =
                    createGroup({ name: tlLabelStr }, dataLabelContainer, dataSet);
    }

    if (!blLabelContainer) {
      blLabelContainer = dataSet.graphics.blLabelContainer =
                    createGroup({ name: blLabelStr }, dataLabelContainer, dataSet);
    }

    if (!trLabelContainer) {
      trLabelContainer = dataSet.graphics.trLabelContainer =
                    createGroup({ name: trLabelStr }, dataLabelContainer, dataSet);
    }

    if (!brLabelContainer) {
      brLabelContainer = dataSet.graphics.brLabelContainer =
                    createGroup({ name: brLabelStr }, dataLabelContainer, dataSet);
    }

    smartLabel.useEllipsesOnOverflow(chartConf.useEllipsesWhenOverflow);
    smartLabel.setStyle(style);

    for (i = 0; i < len; i++) {
      dataObj = dataStore[i];
      // Condition arises when user has removed data in real time update
      if (dataObj === UNDEF) {
        continue;
      }
      graphic = dataObj.graphics;
      // Condition arises when feedData enters less number of data in a dataset compared to the other.
      if (!graphic) {
        continue;
      }

      config = dataObj && dataObj.config;
      setValue = config.setValue;

      if (!conf.mapByCategory && setValue === null) {
        dataSet.removeLabels(dataObj);
        continue;
      }

      displayValue = config.displayValue;

      graphicEle = dataObj.graphics.element;

      pointW = dataObj._width;
      pointH = dataObj._height;
      pointX = dataObj._xPos;
      pointY = dataObj._yPos;

      // Setting style for smartLabel
      smartLabel.setStyle(style);
      // Get the displayValue text from smartlabel
      smartTextObj = smartLabel.getSmartText(displayValue,
        pointW, pointH, false);
      displayValue = smartTextObj.text;

      plotLabelStyle = config.dataLabelStyle;

      // Drawing of displayValue
      if (defined(displayValue) && displayValue !== BLANK && config.showValue) {
        textY = pointY + (pointH * 0.5);
        textX = pointX + (pointW * 0.5);

        attr = {
          text: displayValue,
          title: (smartTextObj.tooltext || BLANKSTRING),
          visibility: config.visible ? visibleStr : hiddenStr,
          fill: plotLabelStyle.color,
          direction: config.textDirection,
          x: textX,
          y: textY,
          cursor: config.setLink ? POINTER : BLANKSTRING,
          'text-bound': [plotLabelStyle.backgroundColor, plotLabelStyle.borderColor,
            style.borderThickness, style.borderPadding,
            style.borderRadius, style.borderDash
          ]
        };

        if (graphicEle) {
          dataObj.graphics.valEle = animationManager.setAnimation({
            el: dataObj.graphics.valEle || 'text',
            container: dataLabelContainer,
            component: dataSet,
            label: 'text',
            attr: attr
          });
          dataObj.graphics.valEle.outlineText(conf.showTextOutLine, attr.fill);
        } else {
          if (dataObj.graphics.valEle) {
            dataObj.graphics.valEle = animationManager.setAnimation({
              el: dataObj.graphics.valEle,
              component: dataSet
            });
          }
        }

        tlLabel = config.tlLabel;
        trLabel = config.trLabel;
        blLabel = config.blLabel;
        brLabel = config.brLabel;

        tlStyle = config.tlLabelStyle;
        trStyle = config.trLabelStyle;
        blStyle = config.blLabelStyle;
        brStyle = config.brLabelStyle;

        isTLLabel = (defined(tlLabel) &&
                        tlLabel !== BLANK);
        isTRLabel = (defined(trLabel) &&
                        trLabel !== BLANK);
        isBLLabel = (defined(blLabel) &&
                        blLabel !== BLANK);
        isBRLabel = (defined(brLabel) &&
                        brLabel !== BLANK);

        maxWidth = pointW * (isTLLabel && isTRLabel
          ? POINT_FIVE : MAX_PERCENT_SPACE);
        maxHeight = (pointH - ((smartTextObj && smartTextObj.height) || 0)) * 0.5;
        // For top labels
        yPos = pointY + GUTTER_4;

        if (isTLLabel) {
          // Setting style for smartLabel
          smartLabel.setStyle(tlStyle);
          smartTextObj = smartLabel.getSmartText(tlLabel,
            maxWidth, maxHeight, false);
          displayValue = smartTextObj.text;
          // Get the x and y position of the dataValue
          xPos = pointX;
          attr = {
            text: displayValue,
            title: (smartTextObj.tooltext || BLANKSTRING),
            visibility: config.visible ? visibleStr : hiddenStr,
            fill: tlStyle.color,
            'font-family': tlStyle.fontFamily,
            'font-weight': tlStyle.fontWeight,
            'line-height': tlStyle.lineHeight,
            'font-size': tlStyle.fontSize,
            'text-anchor': POSITION_START,
            'vertical-align': POSITION_TOP,
            direction: config.textDirection,
            x: xPos + GUTTER_4,
            y: yPos,
            'text-bound': [tlStyle.backgroundColor, tlStyle.borderColor,
              tlStyle.borderThickness, tlStyle.borderPadding,
              tlStyle.borderRadius, tlStyle.borderDash]
          };
          tlLabelCss = Object.assign(attr, tlStyle);
          dataObj.graphics.tlLabel = animationManager.setAnimation({
            el: dataObj.graphics.tlLabel || 'text',
            component: dataSet,
            attr: tlLabelCss,
            container: tlLabelContainer
          });
          dataObj.graphics.tlLabel.outlineText(conf.showTextOutLine, tlLabelCss.fill);
        } else {
          if (dataObj.graphics.tlLabel) {
            dataObj.graphics.tlLabel = animationManager.setAnimation({
              el: dataObj.graphics.tlLabel,
              component: dataSet
            });
          }
        }

        if (isTRLabel) {
          // Setting style for smartRabel
          smartLabel.setStyle(trStyle);
          smartTextObj = smartLabel.getSmartText(trLabel,
            maxWidth, maxHeight, false);
          displayValue = smartTextObj.text;
          // Get the x and y position of the dataValue
          xPos = pointX + pointW;
          attr = {
            text: displayValue,
            title: (smartTextObj.tooltext || BLANKSTRING),
            visibility: config.visible ? visibleStr : hiddenStr,
            fill: trStyle.color,
            'font-family': trStyle.fontFamily,
            'font-weight': trStyle.fontWeight,
            'line-height': trStyle.lineHeight,
            'font-size': trStyle.fontSize,
            'text-anchor': POSITION_END,
            'vertical-align': POSITION_TOP,
            direction: config.textDirection,
            x: xPos - GUTTER_4,
            y: yPos,
            'text-bound': [trStyle.backgroundColor, trStyle.borderColor,
              trStyle.borderThickness, trStyle.borderPadding,
              trStyle.borderRadius, trStyle.borderDash]
          };
          trLabelCss = Object.assign(attr, trStyle);
          dataObj.graphics.trLabel = animationManager.setAnimation({
            el: dataObj.graphics.trLabel || 'text',
            attr: trLabelCss,
            container: trLabelContainer,
            component: dataSet
          });
          dataObj.graphics.trLabel.outlineText(conf.showTextOutLine, trLabelCss.fill);
        } else {
          if (dataObj.graphics.trLabel) {
            dataObj.graphics.trLabel = animationManager.setAnimation({
              el: dataObj.graphics.trLabel,
              component: dataSet
            });
          }
        }

        // For top labels
        yPos = (pointY + pointH) - GUTTER_4;

        if (isBLLabel) {
          // Setting style for smartLabel
          smartLabel.setStyle(blStyle);
          smartTextObj = smartLabel.getSmartText(blLabel,
            maxWidth, maxHeight, false);
          displayValue = smartTextObj.text;
          // Get the x and y position of the dataValue
          xPos = pointX;
          attr = {
            text: displayValue,
            title: (smartTextObj.tooltext || BLANKSTRING),
            visibility: config.visible ? visibleStr : hiddenStr,
            fill: blStyle.color,
            'font-family': blStyle.fontFamily,
            'font-weight': blStyle.fontWeight,
            'line-height': blStyle.lineHeight,
            'font-size': blStyle.fontSize,
            'text-anchor': POSITION_START,
            'vertical-align': POSITION_BOTTOM,
            direction: config.textDirection,
            x: xPos + GUTTER_4,
            y: yPos,
            'text-bound': [blStyle.backgroundColor, blStyle.borderColor,
              blStyle.borderThickness, blStyle.borderPadding,
              blStyle.borderRadius, blStyle.borderDash]
          };
          blLabelCss = Object.assign(attr, blStyle);
          dataObj.graphics.blLabel = animationManager.setAnimation({
            el: dataObj.graphics.blLabel || 'text',
            component: dataSet,
            container: blLabelContainer,
            attr: blLabelCss
          });
          dataObj.graphics.blLabel.outlineText(conf.showTextOutLine, blLabelCss.fill);
        } else {
          if (dataObj.graphics.blLabel) {
            dataObj.graphics.blLabel = animationManager.setAnimation({
              el: dataObj.graphics.blLabel,
              component: dataSet
            });
          }
        }

        if (isBRLabel) {
          // Setting style for smartRabel
          smartLabel.setStyle(blStyle);
          smartTextObj = smartLabel.getSmartText(brLabel,
            maxWidth, maxHeight, false);
          displayValue = smartTextObj.text;
          // Get the x and y position of the dataValue
          xPos = pointX + pointW - GUTTER_4;
          attr = {
            text: displayValue,
            title: (smartTextObj.tooltext || BLANKSTRING),
            visibility: config.visible ? visibleStr : hiddenStr,
            fill: brStyle.color,
            'font-family': brStyle.fontFamily,
            'font-weight': brStyle.fontWeight,
            'line-height': brStyle.lineHeight,
            'font-size': brStyle.fontSize,
            'text-anchor': POSITION_END,
            'vertical-align': POSITION_BOTTOM,
            direction: config.textDirection,
            x: xPos,
            y: yPos,
            'text-bound': [brStyle.backgroundColor, brStyle.borderColor,
              brStyle.borderThickness, brStyle.borderPadding,
              brStyle.borderRadius, brStyle.borderDash]
          };
          brLabelCss = Object.assign(attr, brStyle);
          dataObj.graphics.brLabel = animationManager.setAnimation({
            el: dataObj.graphics.brLabel || 'text',
            attr: brLabelCss,
            container: brLabelContainer,
            component: dataSet
          });
          dataObj.graphics.brLabel.outlineText(conf.showTextOutLine, brLabelCss.fill);
        } else {
          if (dataObj.graphics.brLabel) {
            dataObj.graphics.brLabel = animationManager.setAnimation({
              el: dataObj.graphics.brLabel,
              component: dataSet
            });
          }
        }
      } else {
        dataSet.removeLabels(dataObj);
      }
    }

    dataSet.labelDrawn = true;
  }

  removeLabels (dataObj) {
    let dataSet = this,
      animationManager = dataSet.getFromEnv('animationManager');

    if (dataObj.graphics.valEle) {
      dataObj.graphics.valEle = animationManager.setAnimation({
        el: dataObj.graphics.valEle,
        component: dataSet
      });
    }

    if (dataObj.graphics.tlLabel) {
      dataObj.graphics.tlLabel = animationManager.setAnimation({
        el: dataObj.graphics.tlLabel,
        component: dataSet
      });
    }

    if (dataObj.graphics.trLabel) {
      dataObj.graphics.trLabel = animationManager.setAnimation({
        el: dataObj.graphics.trLabel,
        component: dataSet
      });
    }

    if (dataObj.graphics.blLabel) {
      dataObj.graphics.blLabel = animationManager.setAnimation({
        el: dataObj.graphics.blLabel,
        component: dataSet
      });
    }

    if (dataObj.graphics.brLabel) {
      dataObj.graphics.brLabel = animationManager.setAnimation({
        el: dataObj.graphics.brLabel,
        component: dataSet
      });
    }
  }
  /**
   * Function to remove data elements
   */
  removeDataElems () {
    var dataSet = this,
      components = dataSet.components,
      removeDataArr = components.removeDataArr,
      pool = components.pool || (components.pool = {
        element: [],
        hotElement: [],
        label: []
      }),
      len = removeDataArr.length, // eslint-disable-line good-practices/no-single-usage-variable
      removeData,
      ele,
      graphics,
      i;

    for (i = 0; i < len; i++) {
      removeData = removeDataArr[0];
      removeDataArr.splice(0, 1);
      // In case of non existing data plot continue;
      if (!removeData || !removeData.graphics) {
        continue;
      }

      graphics = removeData.graphics;
      for (ele in graphics) {
        // Stopping any previous animation.
        if (graphics[ele]) {
          graphics[ele] = dataSet.getFromEnv('animationManager').setAnimation({
            el: graphics[ele],
            component: dataSet
          });
        }
      }

      // Storing the graphic elements for reuse.
      removeData.graphics.element && (pool.element = pool.element.concat(removeData.graphics.element));
      removeData.graphics.hotElement && (pool.hotElement = pool.hotElement.concat(removeData.graphics
        .hotElement));
      removeData.graphics.label && (pool.label = pool.label.concat(removeData.graphics.label));
    }
    components.pool = pool;
  }
  /**
   * @returns {number} axis value padding
   */
  getAxisValuePadding () {
    return this.config.defaultPadding;
  }

  /**
   * Function to get the maximum and minimum from dataset
   * @return {Object}     contains max, min value
   */
  getDataLimits () {
    var dataSet = this,
      dataStore = dataSet.components.data,
      conf = dataSet.config,
      i,
      config,
      len = dataStore.length, // eslint-disable-line good-practices/no-single-usage-variable
      setValue,
      maxValue = -Infinity,
      minValue = +Infinity;

    for (i = 0; i < len; i++) {
      if (!dataStore[i] || !Object.keys(dataStore[i]).length) {
        continue;
      }
      config = dataStore[i].config;
      setValue = config.setValue;
      if (setValue !== UNDEF && setValue !== null) {
        maxValue = mathMax(maxValue, setValue);
        minValue = mathMin(minValue, setValue);
      }
    }

    conf.maxValue = maxValue;
    conf.minValue = minValue;
    return {
      max: conf.maxValue,
      min: conf.minValue
    };
  }
  /**
   * function to Remove data plots if the number of current data plots/categories
   * is more than the existing ones.
   * @param  {Object} datasetJSON JSON for dataset configurations
   */
  trimData (datasetJSON) {
    if (!this.config.JSONData) {
      return;
    }
    let dataSet = this,
      config = dataSet.config,
      context = config && config.context,
      prevCatlen = context && context.prevCatlen, // eslint-disable-line good-practices/no-single-usage-variable
      currCatLen = dataSet.getFromEnv('xAxis').getTicksLen(),
      catDiff = prevCatlen - currCatLen,
      prevData = config.JSONData,
      prevDataLength = prevData.data && prevData.data.length, // eslint-disable-line good-practices/no-single-usage-variable
      currDataLength = (datasetJSON.data && datasetJSON.data.length) || 0,
      dataDiff = prevDataLength - currDataLength,
      diff,
      startIndex;

    if (catDiff > dataDiff) {
      diff = catDiff;
      startIndex = currCatLen;
    } else {
      diff = dataDiff;
      startIndex = currDataLength;
    }

    // Removing data plots if the number of current data plots/categories
    // is more than the existing ones.
    if (diff > 0) {
      this.removeData(startIndex, diff, false);
    }
  }
  /**
   * Function for drawing 2D columns.
   * This function is called every time for each dataset when they are initially drawn or shown/hidden from
   * the drawGraph() function.
   */
  draw () {
    var dataSet = this,
      xAxis = dataSet.getFromEnv('xAxis'),
      groupMaxWidth = dataSet.getFromEnv('groupMaxWidth');

    if (!groupMaxWidth) {
      groupMaxWidth = mathAbs(xAxis.getPixel(1) - xAxis.getPixel(0));
      dataSet.addToEnv('groupMaxWidth', groupMaxWidth);
    }

    !dataSet.config.drawn && dataSet.createContainer();

    if (!xAxis.getState('scrolling')) {
      dataSet.createCoordinates();
    }

    // Draw all the graphic elements
    dataSet.drawPlots();
    // dataSet.updatePlot(conf.minValue, conf.maxValue);
  }
  createContainer () {
    var dataset = this,
      parent = dataset.getLinkedParent(),
      group = (dataset.groupName || dataset.dsGroup || dataset.getType()),
      parentContainer,
      parentShadowContainer;

    !parent.getChildContainer(group + 'VcanvasGroup') && (group = 'default');
    parentContainer = parent.getChildContainer(group + 'VcanvasGroup');
    parentShadowContainer = parent.getChildContainer(group + 'ShadowVcanvasGroup');

    !dataset.getContainer('shadowGroup') && dataset.addContainer('shadowGroup',
      createGroup({ name: 'shadow-group' }, parentShadowContainer, dataset));

    !dataset.getContainer('errorShadowGroup') && dataset.addContainer('errorShadowGroup',
      createGroup({ name: 'error-shadow-group' }, parentShadowContainer, dataset));

    !dataset.getContainer('commonElemsGroup') && dataset.addContainer('commonElemsGroup',
      createGroup({ name: 'common-elems-group' }, parentContainer, dataset));

    !dataset.getContainer('plotGroup') && dataset.addContainer('plotGroup',
      createGroup({ name: 'plot-group' }, parentContainer, dataset));

    !dataset.getContainer('errorPlotGroup') && dataset.addContainer('errorPlotGroup',
      createGroup({ name: 'error-plot-group' }, parentContainer, dataset));
    (dataset.getFromEnv('chart').hasAnchor) && dataset.getContainer('errorPlotGroup').insertBefore(dataset.getContainer('plotGroup'));
    !dataset.getContainer('labelGroup') && dataset.addContainer('labelGroup',
      createGroup({
        name: 'label-group',
        'class': 'fusioncharts-datalabels'
      }, parent.getChildContainer('vcanvasLabelGroup'), dataset));
  }
  /**
   * Create pixel coordinates for the plots
   */
  createCoordinates () {
    var dataSet = this,
      chart = dataSet.getFromEnv('chart'),
      isBar = chart.isBar,
      yAxis = dataSet.getFromEnv('yAxis'),
      xAxis = dataSet.getFromEnv('xAxis'),
      yBasePos = yAxis.getPixel(yAxis.getAxisBase()), // eslint-disable-line good-practices/no-single-usage-variable
      dataObj,
      config,
      previousY,
      i,
      Px,
      Py,
      Pb,
      xDepth = chart.config.xDepth || 0,
      yDepth = chart.config.yDepth || 0,
      len = dataSet.components.data.length, // eslint-disable-line good-practices/no-single-usage-variable
      groupManager = dataSet.getLinkedParent(),
      stackConf = groupManager.getstackConf && groupManager.getstackConf(),
      dataStore = dataSet.components.data; // eslint-disable-line good-practices/no-single-usage-variable

    !isBar && (xDepth = -xDepth);
    isBar && (yDepth = -yDepth);

    for (i = 0; i < len; i++) {
      dataObj = dataStore[i];
      config = dataObj && dataObj.config;

      if (dataObj === UNDEF) {
        continue;
      }
      previousY = config._b;
      Px = xAxis.getPixel((stackConf && stackConf[i].x) || config._x) + xDepth;
      Py = yAxis.getPixel(config._y) + yDepth;
      Pb = (previousY ? yAxis.getPixel(previousY) : yBasePos) + yDepth;

      if (xAxis.config.isVertical) {
        config._Px = Py;
        config._Py = Px;
        config._Pby = Px;
        config._Pbx = Pb;
      } else {
        config._Px = Px;
        config._Py = Py;
        config._Pby = Pb;
        config._Pbx = Px;
      }
      if (dataSet.getLineShift) {
        config._Py += dataSet.getLineShift('y');
      }
    }
  }
  /**
   * Helper function of _firePlotEvent which decides single/consolidated tooltip
   * @param {number} plotIndex      plot index
   * @param {Object} e              reference of the original mouse event
   * @param {Object} datasets            column datasets
   */
  _decideTooltipType (plotIndex, e) {
    var dataset = this,
      drawTrendRegion = dataset.getFromEnv('chartConfig').drawTrendRegion,
      toolTipController = dataset.getFromEnv('toolTipController'),
      data = dataset.components.data[plotIndex],
      toolText = data && (data.config.finalTooltext || data.config.toolText),
      element = data && (data.graphics.element || dataset.graphics.sharedAnchor.element), // eslint-disable-line good-practices/no-single-usage-variable
      currentToolTip = dataset.config.currentToolTip,
      originalEvent = e.originalEvent;

    if (drawTrendRegion && element) {
      dataset.config.currentToolTip =
      dataset.getLinkedParent()._drawTooltip(plotIndex, dataset.config.index, originalEvent, currentToolTip);
    } else if (toolText && !drawTrendRegion) {
      if (currentToolTip) {
        toolTipController.draw(originalEvent, toolText, currentToolTip);
      } else {
        currentToolTip = dataset.config.currentToolTip =
          toolTipController.draw(originalEvent, toolText);
      }
    }
  }
  /**
   * This method handles all mouse events of an dataset.
   * @param {string} eventType    name of the event
   * @param {number} plotIndex    index of the plot where this event has been occured
   * @param {Event}  e            reference of the original mouse event
   * @param {Event}  datasets     column dataset
   */
  _firePlotEvent (eventType, plotIndex, e) {
    var dataset = this,
      chart = dataset.getFromEnv('chart'),
      data = dataset.components.data[plotIndex],
      setElement = data.graphics.element,
      labelElement = data.graphics.label,
      // originalEvent = e.originalEvent,
      config,
      setLink;

    if (setElement) {
      config = data.config;
      setLink = config.setLink;
      switch (eventType) {
        case 'fc-mouseover' :
          dataset._decideTooltipType(plotIndex, e);
          _rolloverResponseSetter(chart, data, e, UNDEF, dataset);
          if (setLink) {
            setElement.css({ 'cursor': POINTER });
            // labelElement is undefined in dataset graphics so checking for labelElement added
            labelElement && (labelElement.css({ 'cursor': POINTER }));
          }
          break;
        case 'fc-mouseout' :
          dataset.getFromEnv('toolTipController').hide(dataset.config.currentToolTip);
          _rolloutResponseSetter(chart, data, e, UNDEF, dataset);
          if (setLink) {
            setElement.css({ 'cursor': DEFAULT_CURSOR });
            // labelElement is undefined in dataset graphics so checking for labelElement added
            labelElement && (labelElement.css({ 'cursor': DEFAULT_CURSOR }));
          }
          break;
        case 'fc-click' :
          chart.plotEventHandler(setElement, e);
          break;
        case 'fc-mousemove' :
          dataset._decideTooltipType(plotIndex, e);
      }
    }
  }
  /**
   * Function to remove a data with a given index.
   * @param {number}  indexVal     from which index data would be removed
   * @param {number}  stretchVal   delete count
   * @param {boolean} draw      whether draw or not
   */
  removeData (indexVal, stretchVal, draw) {
    var dataSet = this,
      index = indexVal,
      stretch = stretchVal,
      components = dataSet.components,
      dataStore = components.data,
      removeDataArr = components.removeDataArr || (components.removeDataArr = []),
      conf = dataSet.config,
      i,
      config,
      len,
      maxminFlag = dataSet.maxminFlag;

    stretch === UNDEF && (stretch = 1);
    index = index || 0;

    // Storing the direction of input data for the type of animation to be done during remove.
    if ((index + stretch) === dataStore.length || !dataSet.getFromEnv('chart').isRealTime) {
      dataSet.endPosition = true;
    } else if (index === 0 || index === UNDEF) {
      dataSet.endPosition = false;
    }
    components.removeDataArr = removeDataArr = removeDataArr.concat(dataStore.splice(index, stretch));
    len = removeDataArr.length;

    for (i = len - 1; i >= 0; i--) {
      if (!removeDataArr[i]) {
        removeDataArr.splice(i, 1);
        continue;
      }
      config = removeDataArr[i].config;
      if (config.setValue === conf.maxValue || config.setValue === conf.minValue) {
        maxminFlag = dataSet.maxminFlag = true;
      }
      // Update the x position for this removed elements outside of the canvas
      config._x -= stretch;
      if (maxminFlag) {
        break;
      }
    }
    dataSet.removeDataLen = len;
    dataSet.resetCatPos && dataSet.resetCatPos();
    maxminFlag && dataSet.getDataLimits && dataSet.getDataLimits();
    draw && dataSet.asyncDraw();
  }
}

HeatMapDataset.prototype._configureDataLabelStyle = configureDataLabelStyle;

export default HeatMapDataset;
